/**
 * CSV storage provider.
 *
 * Persists combinations, usage events, favorites and config as **CSV documents**
 * (one per collection). The CSV text is kept in localStorage so it survives
 * reloads on a static deployment (e.g. Cloudflare Pages) with no backend, and
 * each collection can be exported/imported as a real `.csv` file.
 *
 * Column layouts mirror docs/DATABASE_SCHEMA.sql, so an exported file can be
 * bulk-loaded straight into SQL Server / PostgreSQL / Snowflake / Azure SQL when
 * you later graduate from CSV to a database.
 */

import type {
  ActionType,
  Combination,
  ExtensionConfig,
  Favorite,
  ParameterAssignment,
  UsageEvent,
} from "@/types";
import type { StorageProvider } from "./types";
import { toCsv, fromCsv } from "@/lib/utils/csv";

const KEYS = {
  combinations: "spc:csv:combinations",
  events: "spc:csv:events",
  favorites: "spc:csv:favorites",
  config: "spc:csv:config",
} as const;

const COMBINATION_COLUMNS = [
  "id",
  "signature",
  "assignments",
  "usage_count",
  "action_counts",
  "unique_users",
  "created_at",
  "created_by",
  "last_used_at",
  "last_used_by",
  "workbook_name",
  "dashboard_name",
] as const;

const EVENT_COLUMNS = [
  "id",
  "combination_id",
  "signature",
  "action_type",
  "user_name",
  "workbook_name",
  "dashboard_name",
  "event_timestamp",
  "assignments",
] as const;

const FAVORITE_COLUMNS = [
  "id",
  "combination_id",
  "name",
  "pinned",
  "sort_order",
  "created_at",
  "created_by",
] as const;

// ── domain ⇄ CSV row mappers ──────────────────────────────────────────────────

function combinationToRow(c: Combination): Record<string, string> {
  return {
    id: c.id,
    signature: c.signature,
    assignments: JSON.stringify(c.assignments),
    usage_count: String(c.usageCount),
    action_counts: JSON.stringify(c.actionCounts),
    unique_users: JSON.stringify(c.uniqueUsers),
    created_at: c.createdAt,
    created_by: c.createdBy,
    last_used_at: c.lastUsedAt,
    last_used_by: c.lastUsedBy,
    workbook_name: c.workbookName,
    dashboard_name: c.dashboardName,
  };
}

function rowToCombination(r: Record<string, string>): Combination {
  return {
    id: r.id,
    signature: r.signature,
    assignments: parseJson<ParameterAssignment[]>(r.assignments, []),
    usageCount: Number(r.usage_count) || 0,
    actionCounts: parseJson<Record<ActionType, number>>(r.action_counts, {
      Manual_Go: 0,
      Top10_Click: 0,
      Favorite_Click: 0,
      Recent_Click: 0,
      History_Click: 0,
      Recommendation_Click: 0,
    }),
    uniqueUsers: parseJson<string[]>(r.unique_users, []),
    createdAt: r.created_at,
    createdBy: r.created_by,
    lastUsedAt: r.last_used_at,
    lastUsedBy: r.last_used_by,
    workbookName: r.workbook_name,
    dashboardName: r.dashboard_name,
  };
}

function eventToRow(e: UsageEvent): Record<string, string> {
  return {
    id: e.id,
    combination_id: e.combinationId,
    signature: e.signature,
    action_type: e.actionType,
    user_name: e.userName,
    workbook_name: e.workbookName,
    dashboard_name: e.dashboardName,
    event_timestamp: e.timestamp,
    assignments: JSON.stringify(e.assignments),
  };
}

function rowToEvent(r: Record<string, string>): UsageEvent {
  return {
    id: r.id,
    combinationId: r.combination_id,
    signature: r.signature,
    actionType: r.action_type as ActionType,
    userName: r.user_name,
    workbookName: r.workbook_name,
    dashboardName: r.dashboard_name,
    timestamp: r.event_timestamp,
    assignments: parseJson<ParameterAssignment[]>(r.assignments, []),
  };
}

function favoriteToRow(f: Favorite): Record<string, string> {
  return {
    id: f.id,
    combination_id: f.combinationId,
    name: f.name,
    pinned: f.pinned ? "true" : "false",
    sort_order: String(f.order),
    created_at: f.createdAt,
    created_by: f.createdBy,
  };
}

function rowToFavorite(r: Record<string, string>): Favorite {
  return {
    id: r.id,
    combinationId: r.combination_id,
    name: r.name,
    pinned: r.pinned === "true",
    order: Number(r.sort_order) || 0,
    createdAt: r.created_at,
    createdBy: r.created_by,
  };
}

function parseJson<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

// ── reusable serialisers (work from in-memory state, any provider) ────────────

/** Serialise combinations to a CSV document (header + rows). */
export function serializeCombinationsCsv(combinations: Combination[]): string {
  return toCsv([...COMBINATION_COLUMNS], combinations.map(combinationToRow));
}

/** Serialise usage events to a CSV document. */
export function serializeEventsCsv(events: UsageEvent[]): string {
  return toCsv([...EVENT_COLUMNS], events.map(eventToRow));
}

/** Serialise favorites to a CSV document. */
export function serializeFavoritesCsv(favorites: Favorite[]): string {
  return toCsv([...FAVORITE_COLUMNS], favorites.map(favoriteToRow));
}

/** Parse a combinations CSV document back into domain objects. */
export function parseCombinationsCsv(csv: string): Combination[] {
  return fromCsv(csv).map(rowToCombination);
}

export class CsvProvider implements StorageProvider {
  readonly kind = "csv";

  async init(): Promise<void> {
    // Probe localStorage availability; throws (and the factory falls back) if
    // storage is disabled (private windows, locked kiosks).
    const probe = "spc:csv:__probe__";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
  }

  // ── combinations ───────────────────────────────────────────────────────────
  async getCombinations(): Promise<Combination[]> {
    return fromCsv(this.read(KEYS.combinations)).map(rowToCombination);
  }
  async putCombination(combination: Combination): Promise<void> {
    const all = await this.getCombinations();
    const idx = all.findIndex((c) => c.id === combination.id);
    if (idx >= 0) all[idx] = combination;
    else all.push(combination);
    this.write(KEYS.combinations, toCsv([...COMBINATION_COLUMNS], all.map(combinationToRow)));
  }
  async deleteCombination(id: string): Promise<void> {
    const all = (await this.getCombinations()).filter((c) => c.id !== id);
    this.write(KEYS.combinations, toCsv([...COMBINATION_COLUMNS], all.map(combinationToRow)));
  }

  // ── events ─────────────────────────────────────────────────────────────────
  async getUsageEvents(): Promise<UsageEvent[]> {
    return fromCsv(this.read(KEYS.events)).map(rowToEvent);
  }
  async appendUsageEvent(event: UsageEvent): Promise<void> {
    const all = await this.getUsageEvents();
    all.push(event);
    this.write(KEYS.events, toCsv([...EVENT_COLUMNS], all.map(eventToRow)));
  }

  // ── favorites ──────────────────────────────────────────────────────────────
  async getFavorites(): Promise<Favorite[]> {
    return fromCsv(this.read(KEYS.favorites)).map(rowToFavorite);
  }
  async putFavorite(favorite: Favorite): Promise<void> {
    const all = await this.getFavorites();
    const idx = all.findIndex((f) => f.id === favorite.id);
    if (idx >= 0) all[idx] = favorite;
    else all.push(favorite);
    this.write(KEYS.favorites, toCsv([...FAVORITE_COLUMNS], all.map(favoriteToRow)));
  }
  async deleteFavorite(id: string): Promise<void> {
    const all = (await this.getFavorites()).filter((f) => f.id !== id);
    this.write(KEYS.favorites, toCsv([...FAVORITE_COLUMNS], all.map(favoriteToRow)));
  }

  // ── config (small JSON blob, not tabular) ────────────────────────────────────
  async getConfig(): Promise<Partial<ExtensionConfig> | null> {
    return parseJson<Partial<ExtensionConfig> | null>(this.read(KEYS.config), null);
  }
  async putConfig(config: ExtensionConfig): Promise<void> {
    this.write(KEYS.config, JSON.stringify(config));
  }

  async clearAll(): Promise<void> {
    this.write(KEYS.combinations, "");
    this.write(KEYS.events, "");
    this.write(KEYS.favorites, "");
  }

  // ── CSV import / export ──────────────────────────────────────────────────────

  /** Return the raw CSV text for a collection (for download / inspection). */
  exportCsv(collection: "combinations" | "events" | "favorites"): string {
    return this.read(KEYS[collection]);
  }

  /** Replace a collection from a CSV document (e.g. an uploaded file). */
  importCsv(collection: "combinations" | "events" | "favorites", csv: string): void {
    this.write(KEYS[collection], csv.trim());
  }

  private read(key: string): string {
    try {
      return localStorage.getItem(key) ?? "";
    } catch {
      return "";
    }
  }

  private write(key: string, value: string): void {
    localStorage.setItem(key, value);
  }
}
